import React, { useEffect, useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';

import {
  AddToHomeScreenButton,
  Background,
  createGradientOrSolid,
  CustomBackToGameButton,
  overrideCssVars
} from '@appcharge/shared-ui';
import { Checkbox, Divider, Grid, Stack, Typography } from '@mui/material';
import FormControlLabel from '@mui/material/FormControlLabel';
import useFeatureFlags from 'api/useFeatureFlags';
import useImages from 'api/useImages';
import useTheme from 'api/useTheme';
import {
  EAssetType,
  EFeatureFlag,
  EPosition,
  ESectionViewModel,
  StoreBodyBlock
} from 'constants/enums';
import { useFormikContext } from 'formik';
import { UploadsTooltips } from 'hooks/useUpload';
import { mapModelSection } from 'utils/mapModelSection';

import { AcAdvancedTextInput } from 'components/AcAdvancedTextInput/AcAdvancedInput';
import AcCard from 'components/AcCard/AcCard';
import AcGradientInput from 'components/AcGradientInput/AcGradientInput';
import AcInput from 'components/AcInput/AcInput';
import AcSelect from 'components/AcSelect/AcSelect';

import AcSolidInput from '../../AcSolidInput/AcSolidInput';
import { ThemeLoader } from '../ThemeLoader';

import DefaultProfileImgSelect from './components/DefaultProfileImgSelect';
import { Sections } from './components/Sections';
import { StoreTabs } from './components/StoreTabs';
import { StoreThemeBundles } from './components/StoreThemeBundles';
import { SectionsItem, StoreThemeFormValues } from './storeTheme.types';

import 'style/forms.scss';

export const StoreTheme: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { values, handleBlur, handleChange, setFieldValue, errors, touched } =
    useFormikContext<StoreThemeFormValues>();

  const { publisherId } = useParams();

  const {
    getGeneralTheme: generalTheme,
    getStoreScreen: storeScreen,
    getStoreTheme
  } = useTheme(publisherId);

  const getDefaultFormValues = () => ({
    bundleSections: [],
    promotionSections: [],
    addToHomeScreen: {
      active: false,
      verticalOffset: 0,
      buttonImage: '',
      iconImage: 'https://media.appcharge.com/defaults/default-store-logo.svg',
      shortcutName: 'Royal Blast Store',
      borderColor: { colorOne: '#ffffff' },
      backgroundColor: { colorOne: '#522AD8', colorTwo: '' }
    },
    backToGameButton: {
      active: false,
      buttonImage: '',
      position: EPosition.LEFT
    },
    header: { backgroundOpacity: 1 },
    bundleBorderColor: { colorOne: '#000000' },
    buttonColor: { colorOne: '#000000' },
    richText: { text: '', fontColor: '#000000' },
    tabs: []
  });

  const defaultValues = useMemo(() => {
    const defaults = getDefaultFormValues();

    if (!values) {
      return defaults;
    }

    return {
      ...values,
      bundleSections: values.bundleSections || defaults.bundleSections,
      promotionSections: values.promotionSections || defaults.promotionSections,
      addToHomeScreen: values.addToHomeScreen || defaults.addToHomeScreen,
      backToGameButton: values.backToGameButton || defaults.backToGameButton,
      header: values.header || defaults.header,
      bundleBorderColor: values.bundleBorderColor || defaults.bundleBorderColor,
      buttonColor: values.buttonColor || defaults.buttonColor,
      richText: values.richText || defaults.richText,
      tabs: values.tabs || defaults.tabs
    };
  }, [values]);

  const pictures = useImages(publisherId).getImages;
  const [sections, setSections] = useState<SectionsItem[]>(
    defaultValues.bundleSections || []
  );

  const { featureFlags } = useFeatureFlags({ publisherId });

  const showAddToHomeScreen: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.DASHBOARD_ADD_TO_HOME_SCREEN],
    [featureFlags]
  );

  const isTemplatesStudioFeatureEnabled: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.DASHBOARD_TEMPLATES_STUDIO],
    [featureFlags]
  );

  const showRichTextBlock: boolean = useMemo(
    () =>
      getStoreTheme.data?.storeTemplate?.bodyBlocksOrder?.includes(
        StoreBodyBlock.richText
      ) || false,
    [getStoreTheme.data?.storeTemplate?.bodyBlocksOrder]
  );

  const enableTabs: boolean = useMemo(
    () => featureFlags?.[EFeatureFlag.STORE_TABS],
    [featureFlags]
  );

  const SectionLayout =
    mapModelSection[generalTheme?.data?.sectionViewModel as ESectionViewModel];

  useEffect(() => {
    if (
      defaultValues.bundleSections &&
      defaultValues.bundleSections.length > 0
    ) {
      setSections(defaultValues.bundleSections);
    }
  }, [defaultValues.bundleSections]);

  useEffect(() => {
    if (!values) return;

    // ACDEV-10485: move params from general to screen
    const cssMappingOverride = {
      '--button-text-color': values.buttonTextColor,
      '--button-bg-color': createGradientOrSolid(values.buttonColor),
      '--bundle-border-color': createGradientOrSolid(values.bundleBorderColor)
    };

    overrideCssVars(cssMappingOverride, document);
  }, [values?.buttonTextColor, values?.buttonColor, values?.bundleBorderColor]);

  const sectionsData = sections.filter((section) => !section.toBeDeleted);

  const getAddToHomeScreenImgItems = ({
    assetType,
    picturesData,
    imageWidth = 30,
    defaultImageUrl = '',
    defaultAltText = ''
  }: {
    defaultImageUrl?: string;
    defaultAltText?: string;
    assetType: EAssetType;
    picturesData: any[];
    imageWidth?: string | number;
  }) => {
    const createItem = (url: string, alt: string, content = '') => ({
      content,
      key: url,
      value: url,
      renderFunction: () => (
        <Stack direction="row" alignItems="center" gap={1}>
          <img
            src={url}
            alt={alt}
            style={{
              width: imageWidth,
              maxHeight: 30
            }}
          />
        </Stack>
      )
    });

    const defaultItem = defaultImageUrl
      ? createItem(defaultImageUrl, defaultAltText || '')
      : null;

    const filteredItems = picturesData
      .filter((p) => p.type === assetType)
      .map((picture) => createItem(picture.url, picture.name, picture.name));

    return defaultItem ? [defaultItem, ...filteredItems] : filteredItems;
  };

  const hasValues = useMemo(
    () => values && Object.keys(values).length > 0,
    [values]
  );

  useEffect(
    () =>
      setIsLoading(
        storeScreen.isLoading ||
          !storeScreen.data ||
          !hasValues ||
          generalTheme.isLoading ||
          !generalTheme.data ||
          !values
      ),
    [
      storeScreen?.isLoading,
      storeScreen?.data,
      generalTheme.isLoading,
      generalTheme.data,
      hasValues,
      values
    ]
  );

  if (isLoading) {
    return <ThemeLoader />;
  }

  return (
    <Grid container className="formContent">
      <Grid item xs={5}>
        <Typography
          variant="h6"
          sx={{
            marginBottom: '12px',
            fontFamily: 'Inter, Montserrat, serif',
            fontWeight: '600'
          }}
        >
          Store Page
        </Typography>
        {!pictures.isLoading && (
          <Stack>
            {isTemplatesStudioFeatureEnabled ? (
              <>
                {featureFlags?.[EFeatureFlag.STORE_HEADER_V2] && (
                  <>
                    <AcCard
                      stackContainer={false}
                      title="Store Header"
                      description="These settings will apply to your store header"
                    >
                      <Grid container rowSpacing={2} columnSpacing={1.5}>
                        <Grid item xs={12}>
                          <DefaultProfileImgSelect />
                        </Grid>
                      </Grid>
                    </AcCard>
                    <Divider />
                  </>
                )}
              </>
            ) : (
              <>
                <AcCard
                  stackContainer={false}
                  title="Store Header"
                  description="These settings will apply to your store header"
                >
                  <Grid container rowSpacing={2} columnSpacing={1.5}>
                    {featureFlags?.[EFeatureFlag.STORE_HEADER_V2] ? (
                      <>
                        <Grid item xs={6}>
                          <AcSelect
                            header="Background Opacity"
                            name="header.backgroundOpacity"
                            value={`${values.header?.backgroundOpacity}%`}
                            dataTestBoxId="headerBackgroundOpacity"
                            dataTestListId="headerBackgroundOpacityList"
                            optionDataTestIdPrefix="headerBackgroundOpacityOption"
                            items={[
                              { value: 0, content: '0%' },
                              { value: 10, content: '10%' },
                              { value: 20, content: '20%' },
                              { value: 30, content: '30%' },
                              { value: 40, content: '40%' },
                              { value: 50, content: '50%' },
                              { value: 60, content: '60%' },
                              { value: 70, content: '70%' },
                              { value: 80, content: '80%' },
                              { value: 90, content: '90%' },
                              { value: 100, content: '100%' }
                            ]}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={
                              touched.header?.backgroundOpacity &&
                              Boolean(errors.header?.backgroundOpacity)
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <AcSelect
                            header="Logo"
                            name="header.showLogo"
                            value={String(values.header?.showLogo)}
                            dataTestBoxId="headerShowLogo"
                            dataTestListId="headerShowLogoList"
                            optionDataTestIdPrefix="headerShowLogoOption"
                            items={[
                              {
                                content: 'Yes',
                                key: 'true',
                                value: 'true',
                                selected: true
                              },
                              { content: 'No', key: 'false', value: 'false' }
                            ]}
                            onChange={(e) => {
                              e.target.value = e.target.value === 'true';
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.header?.showLogo &&
                              Boolean(errors.header?.showLogo)
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <AcSelect
                            header="Player Name"
                            name="header.showPlayerName"
                            value={String(values.header?.showPlayerName)}
                            dataTestBoxId="headerShowPlayerName"
                            dataTestListId="headerShowPlayerNameList"
                            optionDataTestIdPrefix="headerShowPlayerNameOption"
                            items={[
                              {
                                content: 'Yes',
                                key: 'true',
                                value: 'true',
                                selected: true
                              },
                              {
                                content: 'No',
                                key: 'false',
                                value: 'false'
                              }
                            ]}
                            onChange={(e) => {
                              e.target.value = e.target.value === 'true';
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.header?.showPlayerName &&
                              Boolean(errors.header?.showPlayerName)
                            }
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <DefaultProfileImgSelect />
                        </Grid>
                        <Grid item xs={6}>
                          <AcSelect
                            header="Header Sticky"
                            name="header.isSticky"
                            value={String(values.header?.isSticky)}
                            dataTestBoxId="headerIsSticky"
                            dataTestListId="headerIsStickyList"
                            optionDataTestIdPrefix="headerIsStickyOption"
                            items={[
                              {
                                content: 'Yes',
                                key: 'true',
                                value: 'true'
                              },
                              {
                                content: 'No',
                                key: 'false',
                                value: 'false',
                                selected: true
                              }
                            ]}
                            onChange={(e) => {
                              e.target.value = e.target.value === 'true';
                              handleChange(e);
                            }}
                            onBlur={handleBlur}
                            error={
                              touched.header?.isSticky &&
                              Boolean(errors.header?.isSticky)
                            }
                          />
                        </Grid>
                      </>
                    ) : (
                      <Grid item xs={12}>
                        <DefaultProfileImgSelect />
                      </Grid>
                    )}
                  </Grid>
                </AcCard>
                <Divider />
              </>
            )}
            {!isTemplatesStudioFeatureEnabled && (
              <>
                <AcCard stackContainer={false} title="Banner">
                  <Grid item xs={12}>
                    <AcSelect
                      header="Banner Image"
                      name="defaultBanner"
                      value={values?.defaultBanner}
                      dataTestBoxId="defaultBanner"
                      uploadBtnTestId="uploadDefaultBanner"
                      dataTestListId="defaultBannerList"
                      optionDataTestIdPrefix="defaultBannerOption"
                      onClear={() => {
                        setFieldValue('defaultBanner', '');
                      }}
                      uploadConfig={{
                        onUploadSuccess: async (url: string) => {
                          await pictures.refetch();
                          setFieldValue('defaultBanner', url);
                        },
                        uploadType: EAssetType.BANNER,
                        uploadMessage: UploadsTooltips[EAssetType.BANNER]
                      }}
                      items={[
                        ...(pictures.data || [])
                          .filter((p: any) => p.type === EAssetType.BANNER)
                          .map((picture: any) => {
                            return {
                              content: picture.name,
                              key: picture.url,
                              value: picture.url,
                              renderFunction: () => {
                                return (
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1}
                                  >
                                    <img
                                      src={picture.url}
                                      alt={picture.name}
                                      style={{
                                        width: 30,
                                        maxHeight: 30
                                      }}
                                    />
                                    <Typography>{picture.name}</Typography>
                                  </Stack>
                                );
                              }
                            };
                          })
                      ]}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={
                        touched.defaultBanner && Boolean(errors.defaultBanner)
                      }
                    />
                  </Grid>
                </AcCard>
                <Divider />
              </>
            )}
            <Sections />
            {enableTabs && (
              <>
                <Divider />
                <StoreTabs />
              </>
            )}
            {!isTemplatesStudioFeatureEnabled && (
              <>
                <Divider />
                <AcCard
                  stackContainer={false}
                  title="Store Buttons"
                  description="These settings will apply to buttons throughout your store"
                >
                  <Grid container rowSpacing={2} columnSpacing={1.5}>
                    <Grid item xs={6}>
                      <AcGradientInput
                        header="Button Color"
                        name="buttonColor"
                        defaultValue={values.buttonColor}
                        setValue={setFieldValue}
                        onBlur={handleBlur}
                        dataTestId="buttonColor"
                        dataTestPopoverId="buttonColorPopover"
                        dataTestColorOneId="buttonColorOne"
                        dataTestColorTwoId="buttonColorTwo"
                        dataTestGradientTypeId="buttonColorGradientType"
                        dataTestGradientDirectionId="buttonColorGradientDirection"
                        error={
                          touched.buttonColor?.colorOne &&
                          Boolean(errors.buttonColor?.colorOne)
                        }
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <AcSolidInput
                        header="Button Text Color"
                        name="buttonTextColor"
                        defaultValue={values?.buttonTextColor}
                        setValue={setFieldValue}
                        onBlur={handleBlur}
                        dataTestId="buttonTextColor"
                        dataTestPopoverId="buttonTextColorPopover"
                        dataTestColorPickerId="buttonTextColorPicker"
                        error={
                          touched.buttonTextColor &&
                          Boolean(errors.buttonTextColor)
                        }
                      />
                    </Grid>
                  </Grid>
                </AcCard>
              </>
            )}
            {showAddToHomeScreen && (
              <>
                <Divider />
                <AcCard
                  stackContainer={false}
                  title="Add To Home Screen Button"
                >
                  <Grid container rowSpacing={2} columnSpacing={1.5}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        sx={{ paddingLeft: '10px' }}
                        slotProps={{
                          typography: {
                            fontSize: 14,
                            sx: {
                              marginLeft: '2px'
                            },
                            color: '#717188'
                          }
                        }}
                        control={
                          <Checkbox
                            name="addToHomeScreen.active"
                            checked={defaultValues.addToHomeScreen.active}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setFieldValue(
                                'addToHomeScreen.active',
                                isChecked
                              );
                            }}
                            size="small"
                            sx={{
                              padding: 0,
                              marginRight: '6px',
                              '&.Mui-checked': {
                                color: '#7122FF'
                              }
                            }}
                            inputProps={
                              {
                                'data-testid': 'addToHomeScreenActive'
                              } as React.InputHTMLAttributes<HTMLInputElement>
                            }
                          />
                        }
                        label="Activate this feature in your store"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <AcSolidInput
                        header="Border and Text Color"
                        name="addToHomeScreen.borderColor"
                        defaultValue={
                          defaultValues.addToHomeScreen.borderColor.colorOne
                        }
                        setValue={(fieldName: string, color: string) => {
                          setFieldValue(fieldName, {
                            colorOne: color
                          });
                        }}
                        onBlur={handleBlur}
                        dataTestId="addToHomeScreenBorderColor"
                        dataTestPopoverId="addToHomeScreenBorderColorPopover"
                        dataTestColorPickerId="addToHomeScreenBorderColorPicker"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <AcGradientInput
                        header="Background Color"
                        name="addToHomeScreen.backgroundColor"
                        defaultValue={
                          defaultValues.addToHomeScreen.backgroundColor
                        }
                        setValue={setFieldValue}
                        onBlur={handleBlur}
                        dataTestId="addToHomeScreenBackgroundColor"
                        dataTestPopoverId="addToHomeScreenBackgroundColorPopover"
                        dataTestColorOneId="addToHomeScreenBackgroundColorOne"
                        dataTestColorTwoId="addToHomeScreenBackgroundColorTwo"
                        dataTestGradientTypeId="addToHomeScreenBackgroundColorGradientType"
                        dataTestGradientDirectionId="addToHomeScreenBackgroundColorGradientDirection"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AcSelect
                        placeholder="Button image can be up to 80px in height."
                        header="Button Image"
                        name="addToHomeScreen.buttonImage"
                        value={values.addToHomeScreen?.buttonImage || ''}
                        dataTestBoxId="addToHomeScreenButtonImage"
                        uploadBtnTestId="uploadAddToHomeScreenButtonImage"
                        dataTestListId="addToHomeScreenButtonImageList"
                        optionDataTestIdPrefix="addToHomeScreenButtonImageOption"
                        onClear={() => {
                          setFieldValue('addToHomeScreen.buttonImage', '');
                        }}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={pictures.isLoading || pictures.isRefetching}
                        uploadConfig={{
                          onUploadSuccess: async (url: string) => {
                            await pictures.refetch();
                            setFieldValue('addToHomeScreen.buttonImage', url);
                          },
                          uploadType: EAssetType.ADD_TO_HOME_BUTTON_IMAGE,
                          uploadMessage:
                            UploadsTooltips[EAssetType.ADD_TO_HOME_BUTTON_IMAGE]
                        }}
                        items={getAddToHomeScreenImgItems({
                          assetType: EAssetType.ADD_TO_HOME_BUTTON_IMAGE,
                          picturesData: pictures.data || [],
                          imageWidth: 'auto'
                        })}
                        tooltip="Button image can be up to 80px in height. It will scale down if smaller."
                        error={
                          touched.addToHomeScreen?.buttonImage &&
                          Boolean(errors.addToHomeScreen?.buttonImage)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AcInput
                        header="Vertical Offset (%)"
                        name="addToHomeScreen.verticalOffset"
                        type="number"
                        slotProps={{
                          htmlInput: {
                            max: 100,
                            min: 0
                          }
                        }}
                        defaultValue={
                          defaultValues.addToHomeScreen.verticalOffset
                        }
                        value={values.addToHomeScreen?.verticalOffset}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.addToHomeScreen?.verticalOffset &&
                          Boolean(errors.addToHomeScreen?.verticalOffset)
                        }
                        helperText={
                          touched.addToHomeScreen?.verticalOffset
                            ? errors.addToHomeScreen?.verticalOffset?.toString()
                            : ''
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AcSelect
                        required
                        header="Icon Image"
                        name="addToHomeScreen.iconImage"
                        value={defaultValues.addToHomeScreen.iconImage}
                        dataTestBoxId="addToHomeScreenIconImage"
                        uploadBtnTestId="uploadAddToHomeScreenIconImage"
                        dataTestListId="addToHomeScreenIconImageList"
                        optionDataTestIdPrefix="addToHomeScreenIconImageOption"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={pictures.isLoading || pictures.isRefetching}
                        uploadConfig={{
                          onUploadSuccess: async (url: string) => {
                            await pictures.refetch();
                            setFieldValue('addToHomeScreen.iconImage', url);
                          },
                          uploadType: EAssetType.ADD_TO_HOME_ICON_IMAGE,
                          uploadMessage:
                            UploadsTooltips[EAssetType.ADD_TO_HOME_ICON_IMAGE]
                        }}
                        items={getAddToHomeScreenImgItems({
                          defaultImageUrl:
                            'https://media.appcharge.com/defaults/default-store-logo.svg',
                          defaultAltText: 'Default Appcharge icon',
                          picturesData: pictures.data || [],
                          assetType: EAssetType.ADD_TO_HOME_ICON_IMAGE
                        })}
                        tooltip="Image with 1:1 ratio with max 192px width"
                        error={
                          touched.addToHomeScreen?.iconImage &&
                          Boolean(errors.addToHomeScreen?.iconImage)
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <AcInput
                        required
                        header="Store Title"
                        name="addToHomeScreen.shortcutName"
                        value={defaultValues.addToHomeScreen.shortcutName}
                        tooltip="Will be the name of your home screen icon"
                        type="text"
                        dataTestId="addToHomeScreenShortcutName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched?.addToHomeScreen?.shortcutName &&
                          Boolean(errors?.addToHomeScreen?.shortcutName)
                        }
                        helperText={
                          touched.addToHomeScreen?.shortcutName
                            ? errors.addToHomeScreen?.shortcutName?.toString()
                            : ''
                        }
                      />
                    </Grid>
                  </Grid>
                </AcCard>
              </>
            )}
            <Divider />

            <AcCard stackContainer={false} title="Back To Game Button">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <FormControlLabel
                    sx={{ paddingLeft: '10px' }}
                    slotProps={{
                      typography: {
                        fontSize: 14,
                        sx: {
                          marginLeft: '2px'
                        },
                        color: '#717188'
                      }
                    }}
                    control={
                      <Checkbox
                        name="backToGameButton.active"
                        checked={defaultValues.backToGameButton.active}
                        onChange={(e) => {
                          const isChecked = e.target.checked;
                          setFieldValue('backToGameButton.active', isChecked);
                        }}
                        size="small"
                        sx={{
                          padding: 0,
                          marginRight: '6px',
                          '&.Mui-checked': {
                            color: '#7122FF'
                          }
                        }}
                        inputProps={
                          {
                            'data-testid': 'backToGameButtonActive'
                          } as React.InputHTMLAttributes<HTMLInputElement>
                        }
                      />
                    }
                    label="Activate this feature in your store"
                  />
                </Grid>
                <Grid item xs={12}>
                  <AcSelect
                    placeholder="Button will appear with maximum hight of 40px"
                    header="Button Image"
                    name="backToGameButton.buttonImage"
                    value={defaultValues.backToGameButton?.buttonImage}
                    dataTestBoxId="backToGameButtonImage"
                    uploadBtnTestId="uploadBackToGameButtonImage"
                    dataTestListId="backToGameButtonImageList"
                    optionDataTestIdPrefix="backToGameButtonImageOption"
                    onClear={() => {
                      setFieldValue('backToGameButton.buttonImage', '');
                    }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={pictures.isLoading || pictures.isRefetching}
                    uploadConfig={{
                      onUploadSuccess: async (url: string) => {
                        await pictures.refetch();
                        setFieldValue('backToGameButton.buttonImage', url);
                      },
                      uploadType: EAssetType.BACK_TO_GAME_BUTTON_IMAGE,
                      uploadMessage:
                        UploadsTooltips[EAssetType.BACK_TO_GAME_BUTTON_IMAGE]
                    }}
                    items={getAddToHomeScreenImgItems({
                      assetType: EAssetType.BACK_TO_GAME_BUTTON_IMAGE,
                      picturesData: pictures.data || [],
                      imageWidth: 'auto'
                    })}
                    tooltip="Image will always be with 32px hight"
                    error={
                      touched.backToGameButton?.buttonImage &&
                      Boolean(errors.backToGameButton?.buttonImage)
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <AcSelect
                    placeholder="Select position"
                    header="Button Position"
                    name="backToGameButton.position"
                    value={
                      defaultValues.backToGameButton?.position || EPosition.LEFT
                    }
                    dataTestBoxId="backToGameButtonPosition"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    items={[
                      {
                        content: 'Left',
                        key: EPosition.LEFT,
                        value: EPosition.LEFT
                      },
                      {
                        content: 'Right',
                        key: EPosition.RIGHT,
                        value: EPosition.RIGHT
                      }
                    ]}
                    error={
                      touched.backToGameButton?.position &&
                      Boolean(errors.backToGameButton?.position)
                    }
                  />
                </Grid>
              </Grid>
            </AcCard>

            <Divider />
            <AcCard stackContainer={false} title="Empty State – No Offers">
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcInput
                    headerSize="14"
                    header="Empty State Title"
                    name="noOffersTitleText"
                    value={values.noOffersTitleText}
                    dataTestId="noOffersTitleText"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.noOffersTitleText &&
                      Boolean(errors.noOffersTitleText)
                    }
                    helperText={
                      touched.noOffersTitleText
                        ? errors.noOffersTitleText?.toString()
                        : ''
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <AcInput
                    headerSize="14"
                    header="Empty State Body Text"
                    name="noOffersMessageText"
                    value={values.noOffersMessageText}
                    dataTestId="noOffersMessageText"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={
                      touched.noOffersMessageText &&
                      Boolean(errors.noOffersMessageText)
                    }
                    helperText={
                      touched.noOffersMessageText
                        ? errors.noOffersMessageText?.toString()
                        : ''
                    }
                  />
                </Grid>
              </Grid>
            </AcCard>
            <Divider />
            <AcCard
              stackContainer={false}
              title="Borders"
              description="These settings will apply to borders throughout your store"
            >
              <Grid container rowSpacing={2} columnSpacing={1.5}>
                <Grid item xs={12}>
                  <AcGradientInput
                    header="Bundle Frame Color"
                    name="bundleBorderColor"
                    defaultValue={values.bundleBorderColor}
                    setValue={setFieldValue}
                    onBlur={handleBlur}
                    dataTestId="bundleBorderColor"
                    dataTestPopoverId="bundleBorderColorPopover"
                    dataTestColorOneId="bundleBorderColorOne"
                    dataTestColorTwoId="bundleBorderColorTwo"
                    dataTestGradientTypeId="bundleBorderColorGradientType"
                    dataTestGradientDirectionId="bundleBorderColorGradientDirection"
                    error={
                      touched.bundleBorderColor &&
                      Boolean(errors.bundleBorderColor)
                    }
                    helperText={
                      touched.bundleBorderColor
                        ? errors.bundleBorderColor?.toString()
                        : ''
                    }
                  />
                </Grid>
              </Grid>
            </AcCard>
            {showRichTextBlock && (
              <>
                <Divider />
                <AcCard
                  stackContainer={false}
                  title="Rich Text Block"
                  description="Add custom text content with rich text formatting support"
                >
                  <Grid container rowSpacing={2} columnSpacing={1.5}>
                    <Grid item xs={12}>
                      <AcAdvancedTextInput
                        value={values.richText?.text || ''}
                        setValue={(value) =>
                          setFieldValue('richText.text', value)
                        }
                        error={false}
                        enableColorPicker={true}
                        fontColor={values.richText?.fontColor || 'rgb(0, 0, 0)'}
                      />
                    </Grid>
                  </Grid>
                </AcCard>
              </>
            )}
          </Stack>
        )}
      </Grid>
      <Grid item xs={7} className="iphone-mock-wrapper">
        <div className="button-container">
          <div className="buttons">
            {showAddToHomeScreen && (
              <AddToHomeScreenButton
                borderColor={
                  defaultValues.addToHomeScreen?.borderColor?.colorOne
                }
                backgroundColor={
                  defaultValues.addToHomeScreen.backgroundColor?.colorOne
                    ? defaultValues.addToHomeScreen.backgroundColor
                    : {
                        colorOne: '#522AD8',
                        colorTwo: '#7946FA'
                      }
                }
                buttonImage={defaultValues.addToHomeScreen?.buttonImage}
                onClick={() => {}}
                isMockPreview
              />
            )}
            <CustomBackToGameButton
              buttonImage={defaultValues.backToGameButton?.buttonImage}
              isMockPreview={true}
            />
          </div>
        </div>
        <div
          className="store-preview-wrapper iphone-mock"
          style={{ overflow: 'scroll' }}
        >
          <Background
            backgroundImageMobile={generalTheme.data?.backgroundImageMobile}
            backgroundImageDesktop={generalTheme.data?.backgroundImageMobile}
            width="100%"
            height="100%"
            position="absolute"
          />
          <img
            style={{ position: 'relative' }}
            alt="logo"
            className={`storeLogo ${generalTheme.data?.logoSize}`}
            src={generalTheme.data?.logo}
          />
          <div className="bundles-wrapper">
            {sectionsData.length > 0 ? (
              <>
                {sectionsData.map((section, index) => (
                  <div
                    key={section.publisherSectionId}
                    className="store-theme-sections"
                  >
                    <SectionLayout
                      sectionName={section.name}
                      image={section.image}
                    />
                    <StoreThemeBundles
                      bundleBorderColor={values.bundleBorderColor}
                      buttonTextColor={values.buttonTextColor}
                      buttonColor={values.buttonColor}
                    />
                  </div>
                ))}
              </>
            ) : (
              <StoreThemeBundles
                bundleBorderColor={values.bundleBorderColor}
                buttonTextColor={values.buttonTextColor}
                buttonColor={values.buttonColor}
              />
            )}
          </div>
        </div>
      </Grid>
    </Grid>
  );
};
